// StockDashboard.js — Single-glance stock + purchase health view. Pulls
// /Home/GetStockDashboard which aggregates store items, recent purchases
// and outstanding amounts in one query so the dashboard never feels stale.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress,
  Button,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TodayIcon from '@mui/icons-material/Today';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RefreshIcon from '@mui/icons-material/Refresh';
import config from '../../config';

const KpiCard = ({ icon, label, value, bg, color, sub }) => (
  <motion.div whileHover={{ y: -3 }}>
    <Card sx={{ borderRadius: 3, bgcolor: bg, height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.6)',
              borderRadius: 2,
              p: 0.5,
              display: 'flex',
            }}>
            {React.cloneElement(icon, { sx: { color, fontSize: 22 } })}
          </Box>
          <Typography variant='caption' color='text.secondary'>
            {label}
          </Typography>
        </Box>
        <Typography variant='h4' fontWeight='bold' color={color}>
          {value}
        </Typography>
        {sub && (
          <Typography variant='caption' color='text.secondary'>
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  </motion.div>
);

export default function StockDashboard() {
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const token = sessionStorage.getItem('token');
  const Companycurrency = company?.currency ?? '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboard = async () => {
    if (!company?.id) return;
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/Home/GetStockDashboard/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setData(res.data || null);
    } catch (err) {
      console.error('Dashboard load failed:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmtMoney = (v) => `${Companycurrency}${Number(v || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  if (loading)
    return (
      <Box display='flex' justifyContent='center' mt={10}>
        <CircularProgress />
      </Box>
    );

  if (!data)
    return <Typography align='center' mt={6}>No dashboard data available.</Typography>;

  return (
    <Container maxWidth='xl' sx={{ mt: 3, mb: 6 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <InventoryIcon sx={{ fontSize: 32, color: '#1565c0' }} />
          <Typography variant='h4' fontWeight='bold'>
            Stock Dashboard
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          variant='outlined'
          onClick={() => {
            setRefreshing(true);
            fetchDashboard();
          }}
          disabled={refreshing}>
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>

      {/* KPI row 1 — inventory health */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<InventoryIcon />}
            label='Total Items'
            value={data.totalItems}
            sub={`${data.activeItems} active`}
            bg='#e3f2fd'
            color='#1565c0'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<WarningAmberIcon />}
            label='Low Stock'
            value={data.lowStockCount}
            sub='Below alert level'
            bg='#fff8e1'
            color='#ef6c00'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<ErrorOutlineIcon />}
            label='Out of Stock'
            value={data.outOfStockCount}
            sub='Needs reorder'
            bg='#ffebee'
            color='#c62828'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<AccountBalanceWalletIcon />}
            label='Stock Value'
            value={fmtMoney(data.stockValue)}
            sub='Current stock × last purchase rate'
            bg='#e8f5e9'
            color='#2e7d32'
          />
        </Grid>
      </Grid>

      {/* KPI row 2 — purchase activity */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<TodayIcon />}
            label="Today's Purchase"
            value={fmtMoney(data.todayPurchaseAmount)}
            bg='#f3e5f5'
            color='#6a1b9a'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<CalendarMonthIcon />}
            label='This Month'
            value={fmtMoney(data.monthPurchaseAmount)}
            bg='#e0f7fa'
            color='#00838f'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<ReceiptLongIcon />}
            label='Total Purchase'
            value={fmtMoney(data.totalPurchaseAmount)}
            sub='All-time'
            bg='#e8eaf6'
            color='#3949ab'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<LocalShippingIcon />}
            label='Outstanding'
            value={fmtMoney(data.totalOutstanding)}
            sub={`${data.totalSuppliers} suppliers`}
            bg='#fff3e0'
            color='#e65100'
          />
        </Grid>
      </Grid>

      {/* Low stock + recent purchases side by side */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <WarningAmberIcon sx={{ color: '#ef6c00' }} />
                <Typography variant='subtitle1' fontWeight={700}>
                  Low / Out of Stock
                </Typography>
                <Chip
                  size='small'
                  label={data.lowStockItems?.length || 0}
                  color={data.lowStockItems?.length ? 'warning' : 'default'}
                />
              </Box>
              <Divider sx={{ mb: 1 }} />
              {data.lowStockItems?.length === 0 ? (
                <Typography color='text.secondary' fontSize={13}>
                  All stock levels look healthy. ✅
                </Typography>
              ) : (
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align='right'>
                        Current
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align='right'>
                        Min Alert
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.lowStockItems.map((it) => (
                      <TableRow key={it.id}>
                        <TableCell>{it.storeName}</TableCell>
                        <TableCell align='right'>
                          {it.currentStock} {it.unit}
                        </TableCell>
                        <TableCell align='right'>
                          {it.minStockAlert} {it.unit}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size='small'
                            label={it.status === 'OUT' ? 'OUT OF STOCK' : 'LOW'}
                            color={it.status === 'OUT' ? 'error' : 'warning'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ReceiptLongIcon sx={{ color: '#1565c0' }} />
                <Typography variant='subtitle1' fontWeight={700}>
                  Recent Purchases
                </Typography>
              </Box>
              <Divider sx={{ mb: 1 }} />
              {data.recentPurchases?.length === 0 ? (
                <Typography color='text.secondary' fontSize={13}>
                  No purchases yet. Add your first one from Store Purchase.
                </Typography>
              ) : (
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Vendor</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align='right'>
                        Items
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align='right'>
                        Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.recentPurchases.map((p) => (
                      <TableRow key={p.purchaseId}>
                        <TableCell>#{p.purchaseId}</TableCell>
                        <TableCell>{p.vendorName}</TableCell>
                        <TableCell>
                          {new Date(p.billDate).toLocaleDateString('en-GB')}
                        </TableCell>
                        <TableCell align='right'>{p.itemCount}</TableCell>
                        <TableCell align='right' sx={{ fontWeight: 600 }}>
                          {fmtMoney(p.totalAmount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
